import {
  computed,
  ref,
} from 'vue'

import type { FineUploaderModalUploadHelpers } from '@/composables/current-student-advanced-standing-form/fineUploaderModalUploadHelpers'
import { useUploadAttachmentState } from '@/composables/current-student-advanced-standing-form/useUploadAttachmentState'
import {
  initialWorkExperienceModalFormState,
  newWorkExperienceModalState,
  workExperienceCloseWithoutSavingMessage,
} from '@/models/current-student-advanced-standing-form/workExperienceModal'
import {
  clearModalAlert,
  showModalError,
  type AlertTone,
} from '@/models/current-student-advanced-standing-form/modalAlert'
import type {
  Attachment,
  FineUploaderUpload,
  WorkExperience,
  WorkExperienceEditPayload,
} from '@/models/current-student-advanced-standing-form/api'

type UseWorkExperienceModalControllerOptions = {
  maxAttachments: number
  uploadHelpers: FineUploaderModalUploadHelpers
}

export function useWorkExperienceModalController({
  maxAttachments,
  uploadHelpers,
}: UseWorkExperienceModalControllerOptions) {
  const workExperienceModalVisible = ref(false)
  const workExperienceSaving = ref(false)
  const workExperienceModalError = ref('')
  const workExperienceModalAlertTone = ref<AlertTone>('danger')
  const currentWorkExperience = ref<Partial<WorkExperienceEditPayload> | null>(null)
  const workExperienceYear = ref('')
  const workExperienceShortDescription = ref('')
  const workExperienceLongDescription = ref('')
  const workExperienceAttachmentDeleteIds = ref<number[]>([])
  const workExperienceNewAttachments = ref<FineUploaderUpload[]>([])
  const workExperienceUploadsInProgress = ref(0)
  const workExperienceUploadDropActive = ref(false)
  const workExperienceUploadAlertMessage = ref('')
  const workExperienceConfirmCloseWithoutSave = ref(false)

  const currentWorkExperienceAttachments = computed(() => {
    return currentWorkExperience.value?.experience_attachments ?? []
  })

  const {
    uploadItemLimit: workExperienceUploadItemLimit,
    activeNewUploadCount: activeWorkExperienceNewUploadCount,
    canUploadAttachment: canUploadWorkExperienceAttachment,
    visibleNewAttachments: visibleWorkExperienceNewAttachments,
    showFineUploader: showWorkExperienceFineUploader,
  } = useUploadAttachmentState({
    savedAttachments: currentWorkExperienceAttachments,
    deletedAttachmentIds: workExperienceAttachmentDeleteIds,
    newAttachments: workExperienceNewAttachments,
    maxAttachments,
  })

  function openAddWorkExperienceModal(): void {
    openWorkExperienceModal(newWorkExperienceModalState())
  }

  function openEditWorkExperienceModal(workExperience: WorkExperience): void {
    openWorkExperienceModal(workExperience)
  }

  function openWorkExperienceModal(workExperience: Partial<WorkExperienceEditPayload>): void {
    const formState = initialWorkExperienceModalFormState(workExperience)

    currentWorkExperience.value = formState.currentWorkExperience
    clearModalAlert(workExperienceModalError, workExperienceModalAlertTone)
    workExperienceYear.value = formState.workExperienceYear
    workExperienceShortDescription.value = formState.workExperienceShortDescription
    workExperienceLongDescription.value = formState.workExperienceLongDescription
    workExperienceAttachmentDeleteIds.value = []
    uploadHelpers.revokeUploadThumbnailsFor(workExperienceNewAttachments.value)
    workExperienceNewAttachments.value = []
    workExperienceUploadsInProgress.value = 0
    workExperienceConfirmCloseWithoutSave.value = formState.workExperienceConfirmCloseWithoutSave
    workExperienceModalVisible.value = true
  }

  function closeWorkExperienceModal(): void {
    if (workExperienceConfirmCloseWithoutSave.value && !window.confirm(workExperienceCloseWithoutSavingMessage)) {
      return
    }

    workExperienceModalVisible.value = false
    uploadHelpers.revokeUploadThumbnailsFor(workExperienceNewAttachments.value)
  }

  function onWorkExperienceLongInputChange(): void {
    workExperienceConfirmCloseWithoutSave.value = true
  }

  function closeWorkExperienceUploadAlert(): void {
    uploadHelpers.closeFineUploaderAlert('#workExperienceAttachmentFineUploader', workExperienceUploadAlertMessage)
  }

  function onWorkExperienceUploadDragEnter(event: DragEvent): void {
    uploadHelpers.onUploadDragEnter(event, workExperienceUploadDropActive)
  }

  function onWorkExperienceUploadDragOver(event: DragEvent): void {
    uploadHelpers.onUploadDragOver(event, workExperienceUploadDropActive)
  }

  function onWorkExperienceUploadDragLeave(event: DragEvent): void {
    uploadHelpers.onUploadDragLeave(event, workExperienceUploadDropActive)
  }

  async function onWorkExperienceUploadDrop(event: DragEvent): Promise<void> {
    await uploadHelpers.onUploadDrop(event, workExperienceUploadDropActive, uploadWorkExperienceAttachmentFiles)
  }

  async function onWorkExperienceAttachmentSelected(event: Event): Promise<void> {
    await uploadHelpers.onAttachmentSelected(event, uploadWorkExperienceAttachmentFiles)
  }

  async function uploadWorkExperienceAttachmentFiles(files: File[]): Promise<void> {
    if (files.length === 0) return

    if (activeWorkExperienceNewUploadCount.value + files.length > workExperienceUploadItemLimit.value) {
      await showWorkExperienceUploadLimitAlert(files.length)
      return
    }

    for (const file of files) {
      await uploadWorkExperienceAttachmentFile(file)
    }
  }

  async function showWorkExperienceUploadLimitAlert(addedItems = 1): Promise<void> {
    await uploadHelpers.showUploadLimitAlert(
      '#workExperienceAttachmentFineUploader',
      workExperienceUploadAlertMessage,
      activeWorkExperienceNewUploadCount.value,
      workExperienceUploadItemLimit.value,
      addedItems,
    )
  }

  async function uploadWorkExperienceAttachmentFile(file: File): Promise<void> {
    if (!canUploadWorkExperienceAttachment.value) {
      await showWorkExperienceUploadLimitAlert()
      return
    }

    await uploadHelpers.uploadAttachment(file, workExperienceNewAttachments, workExperienceUploadsInProgress, (message) => {
      showModalError(workExperienceModalError, workExperienceModalAlertTone, message)
    })
  }

  function stageWorkExperienceAttachmentDelete(attachment: Attachment): void {
    if (!workExperienceAttachmentDeleteIds.value.includes(attachment.attachment_id)) {
      workExperienceAttachmentDeleteIds.value.push(attachment.attachment_id)
    }
  }

  async function deleteStagedWorkExperienceAttachment(attachment: FineUploaderUpload): Promise<void> {
    await uploadHelpers.deleteStagedUpload(attachment, (message) => {
      showModalError(workExperienceModalError, workExperienceModalAlertTone, message)
    })
  }

  async function retryWorkExperienceAttachment(attachment: FineUploaderUpload): Promise<void> {
    await uploadHelpers.retryUploadAttachment(attachment, workExperienceUploadsInProgress, (message) => {
      showModalError(workExperienceModalError, workExperienceModalAlertTone, message)
    })
  }

  return {
    workExperienceModalVisible,
    workExperienceSaving,
    workExperienceModalError,
    workExperienceModalAlertTone,
    currentWorkExperience,
    workExperienceYear,
    workExperienceShortDescription,
    workExperienceLongDescription,
    workExperienceAttachmentDeleteIds,
    workExperienceNewAttachments,
    workExperienceUploadsInProgress,
    workExperienceUploadDropActive,
    workExperienceUploadAlertMessage,
    workExperienceConfirmCloseWithoutSave,
    currentWorkExperienceAttachments,
    visibleWorkExperienceNewAttachments,
    showWorkExperienceFineUploader,
    openAddWorkExperienceModal,
    openEditWorkExperienceModal,
    openWorkExperienceModal,
    closeWorkExperienceModal,
    onWorkExperienceLongInputChange,
    closeWorkExperienceUploadAlert,
    onWorkExperienceUploadDragEnter,
    onWorkExperienceUploadDragOver,
    onWorkExperienceUploadDragLeave,
    onWorkExperienceUploadDrop,
    onWorkExperienceAttachmentSelected,
    uploadWorkExperienceAttachmentFiles,
    showWorkExperienceUploadLimitAlert,
    uploadWorkExperienceAttachmentFile,
    stageWorkExperienceAttachmentDelete,
    deleteStagedWorkExperienceAttachment,
    retryWorkExperienceAttachment,
  }
}
